/**
 * MyDay local AI helpers.
 *
 * These are heuristic / template generators that run entirely in the
 * browser so the AI UX is fully wired before any Claude API key is
 * provided. When ANTHROPIC_API_KEY is configured on the server, swap
 * these calls for real streaming requests without changing the UI.
 */
import {
  useWorkspaceStore, STATUS_OPTIONS, PRIORITY_OPTIONS, PEOPLE,
  type Board, type Item,
} from '@/stores/workspaceStore';
import type { ColumnType } from '@myday/shared';

// ========================================================================
// Board generator
// ========================================================================

export interface GeneratedBoard {
  name: string;
  icon: string;
  color: string;
  description: string;
  groups: string[];
  columns: { name: string; type: ColumnType }[];
  items: { group: number; name: string; status?: string; priority?: string; person?: string; dateOffset?: number }[];
}

interface Template {
  match: RegExp | string[];
  icon: string;
  color: string;
  groups: string[];
  items: Omit<GeneratedBoard['items'][number], 'group'>[] | ((groups: string[]) => GeneratedBoard['items']);
  columns?: GeneratedBoard['columns'];
}

const DEFAULT_COLUMNS: GeneratedBoard['columns'] = [
  { name: 'Status',   type: 'status' },
  { name: 'Person',   type: 'person' },
  { name: 'Priority', type: 'priority' },
  { name: 'Due date', type: 'date' },
  { name: 'Notes',    type: 'text' },
];

const TEMPLATES: Template[] = [
  // ---------- Marketing / Launch ----------
  {
    match: /(launch|campaign|marketing|go-?to-?market|gtm|promo)/i,
    icon: '',
    color: 'from-neon-pink to-neon-purple',
    groups: ['Planning', 'In progress', 'Done'],
    items: (groups) => buildItems(groups, [
      ['Define launch audience',          0, { status: 'working', priority: 'high' }],
      ['Build messaging doc',             0, { status: 'working', priority: 'high' }],
      ['Design landing page',             1, { status: 'working', priority: 'high' }],
      ['Produce launch video',            1, { status: 'not_started', priority: 'medium' }],
      ['Write blog post',                 1, { status: 'not_started', priority: 'medium' }],
      ['Prepare social assets',           1, { status: 'not_started', priority: 'medium' }],
      ['Schedule press outreach',         0, { status: 'not_started', priority: 'medium' }],
      ['Set up email sequence',           1, { status: 'not_started', priority: 'high' }],
      ['Configure analytics',             1, { status: 'not_started', priority: 'medium' }],
      ['Internal training session',       0, { status: 'not_started', priority: 'low' }],
      ['Kickoff meeting',                 2, { status: 'done', priority: 'low' }],
      ['Finalize budget',                 2, { status: 'done', priority: 'medium' }],
    ]),
  },

  // ---------- Product roadmap ----------
  {
    match: /(roadmap|product|feature|epic|initiative)/i,
    icon: '',
    color: 'from-neon-blue to-neon-cyan',
    groups: ['Now', 'Next', 'Later'],
    items: (groups) => buildItems(groups, [
      ['User onboarding rework',     0, { status: 'working', priority: 'high' }],
      ['Real-time collab',           0, { status: 'working', priority: 'critical' }],
      ['Mobile notifications',       0, { status: 'stuck',   priority: 'high' }],
      ['Automations v1',             0, { status: 'working', priority: 'critical' }],
      ['AI assistant',               1, { status: 'not_started', priority: 'high' }],
      ['Advanced filters',           1, { status: 'not_started', priority: 'medium' }],
      ['SSO / SAML',                 1, { status: 'not_started', priority: 'medium' }],
      ['Analytics dashboard',        1, { status: 'not_started', priority: 'medium' }],
      ['Offline mode',               2, { status: 'not_started', priority: 'low' }],
      ['iPad app',                   2, { status: 'not_started', priority: 'low' }],
      ['Audit log + compliance',     2, { status: 'not_started', priority: 'medium' }],
    ]),
  },

  // ---------- Hiring ----------
  {
    match: /(hiring|recruit|candidate|interview|talent)/i,
    icon: '',
    color: 'from-neon-magenta to-neon-pink',
    groups: ['Sourcing', 'Interviewing', 'Offer', 'Hired'],
    items: (groups) => buildItems(groups, [
      ['Write JD for Senior Engineer', 0, { status: 'working',     priority: 'high' }],
      ['Post on LinkedIn + boards',    0, { status: 'working',     priority: 'medium' }],
      ['Reach out to passive leads',   0, { status: 'not_started', priority: 'medium' }],
      ['Review applications (batch 1)',0, { status: 'working',     priority: 'high' }],
      ['Phone screen — Candidate A',   1, { status: 'working',     priority: 'high' }],
      ['Technical — Candidate A',      1, { status: 'not_started', priority: 'high' }],
      ['Team interview — Candidate B', 1, { status: 'not_started', priority: 'medium' }],
      ['Reference checks',             2, { status: 'not_started', priority: 'medium' }],
      ['Prepare offer letter',         2, { status: 'not_started', priority: 'high' }],
      ['Onboarding plan',              3, { status: 'not_started', priority: 'low' }],
    ]),
  },

  // ---------- Sprint / Eng ----------
  {
    match: /(sprint|bug|engineer|dev|ticket|issue|backlog|standup|qa)/i,
    icon: '',
    color: 'from-neon-yellow to-neon-orange',
    groups: ['This sprint', 'Next sprint', 'Icebox'],
    items: (groups) => buildItems(groups, [
      ['Fix auth redirect bug',          0, { status: 'working',     priority: 'critical' }],
      ['Performance: large board render',0, { status: 'working',     priority: 'high' }],
      ['Polish dashboard cards',         0, { status: 'working',     priority: 'medium' }],
      ['Add empty states',               0, { status: 'done',        priority: 'low' }],
      ['Refactor column resizer',        1, { status: 'not_started', priority: 'low' }],
      ['Dark mode QA pass',              1, { status: 'not_started', priority: 'medium' }],
      ['Upgrade Node 22',                1, { status: 'not_started', priority: 'medium' }],
      ['Rewrite legacy search',          2, { status: 'not_started', priority: 'low' }],
    ]),
  },

  // ---------- Events ----------
  {
    match: /(event|conference|summit|meetup|workshop|party|retreat)/i,
    icon: '',
    color: 'from-neon-violet to-neon-magenta',
    groups: ['6+ weeks out', '2–6 weeks', '1–2 weeks', 'Day of', 'Post-event'],
    items: (groups) => buildItems(groups, [
      ['Confirm venue + date',      0, { status: 'working',     priority: 'high' }],
      ['Finalize speaker list',     0, { status: 'not_started', priority: 'high' }],
      ['Open registration',         1, { status: 'not_started', priority: 'high' }],
      ['Build event website',       1, { status: 'not_started', priority: 'medium' }],
      ['Catering + dietary',        2, { status: 'not_started', priority: 'medium' }],
      ['Signage + swag',            2, { status: 'not_started', priority: 'medium' }],
      ['A/V rehearsal',             3, { status: 'not_started', priority: 'high' }],
      ['Check-in desk ready',       3, { status: 'not_started', priority: 'medium' }],
      ['Thank-you email',           4, { status: 'not_started', priority: 'low' }],
      ['Feedback survey',           4, { status: 'not_started', priority: 'medium' }],
    ]),
  },

  // ---------- Content ----------
  {
    match: /(content|blog|newsletter|editorial|article|writing|social)/i,
    icon: '',
    color: 'from-neon-teal to-neon-lime',
    groups: ['Ideas', 'Drafting', 'Editing', 'Scheduled', 'Published'],
    items: (groups) => buildItems(groups, [
      ['"How we built MyDay in 30 days"', 1, { status: 'working',     priority: 'high' }],
      ['"10 workflows to try this week"', 0, { status: 'not_started', priority: 'medium' }],
      ['"A love letter to sticky notes"', 2, { status: 'stuck',       priority: 'low' }],
      ['Weekly changelog #12',            3, { status: 'not_started', priority: 'medium' }],
      ['Social teaser thread',            0, { status: 'not_started', priority: 'low' }],
      ['Case study — Dusk Labs',          1, { status: 'working',     priority: 'medium' }],
    ]),
  },

  // ---------- OKRs / Strategy ----------
  {
    match: /(okr|goal|strategy|objective|q[1-4])/i,
    icon: '',
    color: 'from-neon-red to-neon-magenta',
    groups: ['Objective 1', 'Objective 2', 'Objective 3'],
    items: (groups) => buildItems(groups, [
      ['KR1 — 10k signups this quarter',  0, { status: 'working', priority: 'high' }],
      ['KR2 — 40% activation rate',       0, { status: 'working', priority: 'high' }],
      ['KR3 — NPS > 40',                  0, { status: 'not_started', priority: 'medium' }],
      ['KR1 — Ship AI assistant',         1, { status: 'working', priority: 'critical' }],
      ['KR2 — Launch mobile app',         1, { status: 'not_started', priority: 'high' }],
      ['KR1 — $X MRR',                    2, { status: 'working', priority: 'high' }],
      ['KR2 — Launch enterprise tier',    2, { status: 'not_started', priority: 'medium' }],
    ]),
  },
];

function buildItems(
  _groups: string[],
  rows: [string, number, Partial<Omit<GeneratedBoard['items'][number], 'name' | 'group'>>?][],
): GeneratedBoard['items'] {
  return rows.map(([name, group, extra]) => ({
    group,
    name,
    status: extra?.status ?? 'not_started',
    priority: extra?.priority ?? 'medium',
    person: PEOPLE[Math.floor(Math.random() * PEOPLE.length)].id,
    dateOffset: Math.floor(Math.random() * 30) - 2,
  }));
}

function fallbackGenerator(prompt: string): Template {
  return {
    match: /.*/,
    icon: '',
    color: 'from-neon-purple to-neon-blue',
    groups: ['To do', 'Doing', 'Done'],
    items: (groups) => buildItems(groups, [
      [`Kickoff — ${prompt.slice(0, 40)}`,   0, { status: 'working', priority: 'high' }],
      ['Define scope + success metrics',      0, { status: 'working', priority: 'high' }],
      ['Identify stakeholders',               0, { status: 'not_started', priority: 'medium' }],
      ['Draft plan document',                 1, { status: 'working', priority: 'medium' }],
      ['First milestone',                     1, { status: 'not_started', priority: 'medium' }],
      ['Checkpoint with team',                1, { status: 'not_started', priority: 'low' }],
      ['Wrap-up + retro',                     2, { status: 'not_started', priority: 'low' }],
    ]),
  };
}

export function generateBoard(prompt: string): GeneratedBoard {
  const trimmed = prompt.trim();
  const template = TEMPLATES.find((t) => t.match instanceof RegExp ? t.match.test(trimmed) : false) ?? fallbackGenerator(trimmed);

  const groups = template.groups;
  const columns = template.columns ?? DEFAULT_COLUMNS;
  const items = typeof template.items === 'function' ? template.items(groups) : template.items.map((i, idx) => ({ ...i, group: idx % groups.length }));

  // Use the prompt as the board name if the user gave something meaningful,
  // otherwise use a friendly default.
  const name = cleanName(trimmed) || defaultNameFor(template);

  return {
    name,
    icon: template.icon,
    color: template.color,
    description: `Auto-generated from prompt: "${trimmed}"`,
    groups,
    columns,
    items,
  };
}

function cleanName(s: string) {
  return s
    .replace(/^(create|build|make|plan|draft|set ?up)\s+(a|an|the)?\s+/i, '')
    .replace(/^board\s*:?\s*/i, '')
    .replace(/\s+/g, ' ')
    .slice(0, 80)
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function defaultNameFor(t: Template) {
  if (t.icon === '') return 'Launch plan';
  if (t.icon === '') return 'Product roadmap';
  if (t.icon === '') return 'Hiring pipeline';
  if (t.icon === '') return 'Sprint plan';
  if (t.icon === '') return 'Event plan';
  if (t.icon === '') return 'Content calendar';
  if (t.icon === '') return 'OKRs';
  return 'New project';
}

export function materializeBoard(g: GeneratedBoard): string {
  const store = useWorkspaceStore.getState();
  const boardId = store.createBoard({ name: g.name, icon: g.icon, color: g.color });

  // Replace the default groups / columns created by createBoard with ours
  const state = useWorkspaceStore.getState();
  const existingGroups = state.groups.filter((gr) => gr.boardId === boardId);
  existingGroups.forEach((gr) => state.deleteGroup(gr.id));
  const existingColumns = state.columns.filter((co) => co.boardId === boardId);
  existingColumns.forEach((co) => state.deleteColumn(co.id));

  // Create groups
  const groupIds = g.groups.map((name) => state.createGroup(boardId, name));

  // Create columns
  const columnIds: Record<string, string> = {};
  for (const c of g.columns) {
    const id = state.createColumn(boardId, { name: c.name, type: c.type });
    columnIds[c.type] = id;
  }

  // Create items
  for (const it of g.items) {
    const gid = groupIds[Math.min(it.group, groupIds.length - 1)];
    const itemId = state.createItem(boardId, gid, it.name);
    const svc = useWorkspaceStore.getState();
    if (columnIds.status   && it.status)   svc.updateItemValue(itemId, columnIds.status, it.status);
    if (columnIds.priority && it.priority) svc.updateItemValue(itemId, columnIds.priority, it.priority);
    if (columnIds.person   && it.person)   svc.updateItemValue(itemId, columnIds.person, it.person);
    if (columnIds.date     && typeof it.dateOffset === 'number') {
      const d = new Date(); d.setDate(d.getDate() + it.dateOffset);
      svc.updateItemValue(itemId, columnIds.date, d.toISOString().slice(0, 10));
    }
  }

  return boardId;
}

// ========================================================================
// Summarize a board
// ========================================================================

export function summarizeBoard(boardId: string): string {
  const state = useWorkspaceStore.getState();
  const board = state.boards.find((b) => b.id === boardId);
  if (!board) return 'I couldn\'t find that board.';
  const items = state.items.filter((i) => i.boardId === boardId);
  const columns = state.columns.filter((c) => c.boardId === boardId);
  const groups = state.groups.filter((g) => g.boardId === boardId);
  const statusCol = columns.find((c) => c.type === 'status');
  const personCol = columns.find((c) => c.type === 'person');
  const dateCol = columns.find((c) => c.type === 'date');

  const total = items.length;
  if (total === 0) return `${board.icon} **${board.name}** is empty. Add some items to get started.`;

  const byStatus: Record<string, Item[]> = {};
  for (const it of items) {
    const st = statusCol ? (it.values[statusCol.id] as string) : 'not_started';
    (byStatus[st ?? 'not_started'] ??= []).push(it);
  }

  const done      = byStatus.done?.length      ?? 0;
  const working   = byStatus.working?.length   ?? 0;
  const stuck     = byStatus.stuck?.length     ?? 0;
  const notStarted= byStatus.not_started?.length ?? 0;
  const donePct = Math.round((done / total) * 100);

  // Overdue
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const overdue = dateCol ? items.filter((it) => {
    const d = it.values[dateCol.id] as string;
    if (!d) return false;
    const day = new Date(d); day.setHours(0, 0, 0, 0);
    const isDone = statusCol && it.values[statusCol.id] === 'done';
    return day < todayStart && !isDone;
  }).length : 0;

  // Top assignees
  const workload = new Map<string, number>();
  if (personCol) {
    for (const it of items) {
      const pid = it.values[personCol.id] as string;
      if (!pid) continue;
      workload.set(pid, (workload.get(pid) ?? 0) + 1);
    }
  }
  const topPeople = Array.from(workload.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([pid, count]) => {
      const person = PEOPLE.find((p) => p.id === pid);
      return `${person?.name ?? 'Someone'} (${count})`;
    })
    .join(', ');

  const lines: string[] = [];
  lines.push(`${board.icon} **${board.name}** — ${total} items across ${groups.length} group${groups.length === 1 ? '' : 's'}.`);
  lines.push('');
  lines.push(`**Progress:** ${donePct}% done (${done} done · ${working} working · ${stuck} stuck · ${notStarted} not started).`);
  if (overdue > 0) lines.push(` **${overdue} overdue** item${overdue === 1 ? '' : 's'} need attention.`);
  if (topPeople)   lines.push(`**Top contributors:** ${topPeople}.`);

  // Standout items
  if (stuck > 0) {
    lines.push('');
    lines.push(`**Stuck items to unblock:**`);
    for (const it of (byStatus.stuck ?? []).slice(0, 3)) {
      lines.push(`• ${it.name}`);
    }
  }

  return lines.join('\n');
}

// ========================================================================
// Ask the data (simple NL matcher)
// ========================================================================

export interface AskAnswer {
  text: string;
  items?: { id: string; boardId: string; name: string }[];
}

export function askData(q: string): AskAnswer {
  const question = q.trim().toLowerCase();
  const state = useWorkspaceStore.getState();
  const items = state.items;
  const columns = state.columns;
  const boards = state.boards;

  // --- Count by person ---
  for (const p of PEOPLE) {
    if (question.includes(p.name.toLowerCase()) || question.includes(p.id.replace('u_', ''))) {
      const theirItems = items.filter((it) => {
        const personCol = columns.find((c) => c.boardId === it.boardId && c.type === 'person');
        return personCol && it.values[personCol.id] === p.id;
      });
      const done = theirItems.filter((it) => {
        const statusCol = columns.find((c) => c.boardId === it.boardId && c.type === 'status');
        return statusCol && it.values[statusCol.id] === 'done';
      });
      return {
        text: `**${p.name}** has **${theirItems.length} item${theirItems.length === 1 ? '' : 's'}** assigned (${done.length} done).`,
        items: theirItems.slice(0, 10).map((it) => ({ id: it.id, boardId: it.boardId, name: it.name })),
      };
    }
  }

  // --- Overdue ---
  if (/overdue|late/i.test(question)) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const overdue = items.filter((it) => {
      const dateCol = columns.find((c) => c.boardId === it.boardId && c.type === 'date');
      const statusCol = columns.find((c) => c.boardId === it.boardId && c.type === 'status');
      const d = dateCol ? (it.values[dateCol.id] as string) : null;
      const s = statusCol ? (it.values[statusCol.id] as string) : null;
      return d && new Date(d) < today && s !== 'done';
    });
    return {
      text: overdue.length === 0 ? 'Nothing overdue  Everything\'s on track.' : `**${overdue.length}** item${overdue.length === 1 ? '' : 's'} overdue.`,
      items: overdue.slice(0, 15).map((it) => ({ id: it.id, boardId: it.boardId, name: it.name })),
    };
  }

  // --- Done this week ---
  if (/done|completed|shipped|finished/i.test(question) && /(week|recent|this)/i.test(question)) {
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    const done = items.filter((it) => {
      const statusCol = columns.find((c) => c.boardId === it.boardId && c.type === 'status');
      return statusCol && it.values[statusCol.id] === 'done' && new Date(it.updatedAt) >= weekAgo;
    });
    return {
      text: `**${done.length}** item${done.length === 1 ? '' : 's'} marked done in the last 7 days. Nice.`,
      items: done.slice(0, 15).map((it) => ({ id: it.id, boardId: it.boardId, name: it.name })),
    };
  }

  // --- Critical / High priority ---
  if (/critical|high|urgent|priority/i.test(question)) {
    const high = items.filter((it) => {
      const priorityCol = columns.find((c) => c.boardId === it.boardId && c.type === 'priority');
      if (!priorityCol) return false;
      const p = it.values[priorityCol.id];
      return p === 'critical' || p === 'high';
    });
    return {
      text: `**${high.length}** high-priority item${high.length === 1 ? '' : 's'} across all boards.`,
      items: high.slice(0, 15).map((it) => ({ id: it.id, boardId: it.boardId, name: it.name })),
    };
  }

  // --- Per-board count ---
  for (const b of boards) {
    if (question.includes(b.name.toLowerCase())) {
      const bItems = items.filter((it) => it.boardId === b.id);
      return {
        text: `${b.icon} **${b.name}** has **${bItems.length} items** in ${state.groups.filter((g) => g.boardId === b.id).length} groups.`,
        items: bItems.slice(0, 10).map((it) => ({ id: it.id, boardId: it.boardId, name: it.name })),
      };
    }
  }

  // --- How many total ---
  if (/how many|count|total/i.test(question)) {
    return {
      text: `You have **${items.length} items** across **${boards.length} boards** · **${PEOPLE.length} people** on the team.`,
    };
  }

  // --- Who has the most ---
  if (/(who|most|busiest|heaviest)/i.test(question)) {
    const counts = PEOPLE.map((p) => {
      const n = items.filter((it) => {
        const personCol = columns.find((c) => c.boardId === it.boardId && c.type === 'person');
        return personCol && it.values[personCol.id] === p.id;
      }).length;
      return { person: p, n };
    }).sort((a, b) => b.n - a.n);
    const top = counts[0];
    return { text: `**${top.person.name}** has the most items assigned (${top.n}). Close behind: ${counts.slice(1, 3).map((c) => `${c.person.name} (${c.n})`).join(', ')}.` };
  }

  return {
    text:
`I\'m not sure how to answer that yet. Try asking me:
• "How many items does Maya have?"
• "What's overdue?"
• "Who's the busiest right now?"
• "What's done this week?"
• "How many high priority items?"`,
  };
}

// ========================================================================
// Helpers exposed to other components
// ========================================================================

export function scopeItemsByBoard(boardId?: string): Item[] {
  const state = useWorkspaceStore.getState();
  return boardId ? state.items.filter((i) => i.boardId === boardId) : state.items;
}

export { STATUS_OPTIONS, PRIORITY_OPTIONS };
export type { Board };
